import {HttpClient} from '@angular/common/http';
import {EventEmitter, Injectable} from '@angular/core';
import * as FileSaver from 'file-saver';
import {ToastrService} from 'ngx-toastr';
import {ReplaySubject} from 'rxjs';
import {AdditionalDocument} from '../models/additional-document';
import {AgreementDocument} from '../models/agreement-document';
import {GeneralAgreement} from '../models/general-agreement';
import {SocialConfig} from '../models/social-config';
import {UpdateUserResult} from '../models/update-user-result';
import {User} from '../models/user';
import {UserStore} from '../models/user-store';
import {ValidateResult} from '../models/validate-result';
import {AuthService} from '../repositories/auth.service';
import {LoadingDataService} from '../repositories/loading-data.service';
import {ValidationInfoService} from '../repositories/validation-info.service';
import {BrowserStorageService} from '../services/browser-storage.service';
import {BaseDataService} from './base-data.service';

@Injectable({
  providedIn: 'root',
})
export class BusinessDataService extends BaseDataService {
  private userModel: User;
  private _user = new ReplaySubject<UserStore>(1);
  private _requestForUpdateUser = new EventEmitter();

  constructor(
    client: HttpClient,
    private validationInfo: ValidationInfoService,
    public toastrMgr: ToastrService,
    public loadingService: LoadingDataService,
    private storageService: BrowserStorageService,
    private authService: AuthService
  ) {
    super(client, loadingService);
  }

  public SetUser(u: UserStore): void {
    this.userModel = u.model;
    this._user.next(u);
  }

  public GetUser(): ReplaySubject<UserStore> {
    return this._user;
  }

  public getUserModel() {
    return this.userModel;
  }

  public requestForUpdateUser(): EventEmitter<any> {
    return this._requestForUpdateUser;
  }

  public getUser(phone: string, delegate: (user: User) => void): void {
    this.loadingService.loadingUserData(true);
    const langCode = sessionStorage.getItem('lang')
      ? sessionStorage.getItem('lang')
      : 'en';
    this.get<any>(
      'business/getuser?phone=' + phone + '&langCode=' + langCode,
      (response) => {
        this.loadingService.loadingUserData(false);
        delegate(response);
      }
    );
  }

  public getAgreements(delegate: (docs: Array<GeneralAgreement>) => void) {
    const langCode = this.storageService.getLocal('lang')
      ? this.storageService.getLocal('lang')
      : 'en';

    this.get<Array<GeneralAgreement>>(
      'business/getgeneralagreements?langCode=' + langCode,
      (response) => {
        delegate(response);
      }
    );
  }

  public updateUserWithoutValidation(user: User, delegate?: () => void): void {
    this._requestForUpdateUser.emit({
      user,
      immediately: false,
      action: delegate,
    });
  }

  public updateUserImmediatly(user: User, delegate?: () => void, errorDelegate?: (error: any) => void): void {
    this.validationInfo.onUserUpdate.emit({});
    this.loadingService.loadingOtherData(true);
    if (this.validationInfo.getValidateState()) {
      this._requestForUpdateUser.emit({
        user,
        immediately: true,
        action: delegate,
        errorAction: errorDelegate,
      });
    } else if (errorDelegate) {
      this.loadingService.loadingOtherData(false);
      errorDelegate(new Error('local-validation-failed'));
    }
  }

  public sendUserUpdate(
    user: User,
    delegate: (result: UpdateUserResult) => void,
    errorDelegate?: (error: any) => void
  ): void {
    this.post<UpdateUserResult>(
      `business/updateuser?isAdmin=${this.authService.isAdmin}`,
      user,
      (t) => {
        this.loadingService.loadingOtherData(false);
        delegate(t);
      },
      errorDelegate
    );
  }

  public uploadDocument(
    document: AdditionalDocument,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.post<AdditionalDocument>(
      'business/uploadDocument',
      document,
      (response) => {
        this.loadingService.loadingOtherData(false);
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public uploadSecondHolderDocument(
    document: AdditionalDocument,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.post<AdditionalDocument>(
      'business/uploadSecondHolderDocument',
      document,
      (response) => {
        this.loadingService.loadingOtherData(false);
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public removeDocument(
    id: number,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.get<AdditionalDocument>(
      `business/removeDocument?id=${id}`,
      (response) => {
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public removeSecondHolderDocument(
    id: number,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.get<AdditionalDocument>(
      `business/removeSecondHolderDocument?id=${id}`,
      (response) => {
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public updateDocument(
    document: AdditionalDocument,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.post<AdditionalDocument>(
      'business/updateDocument',
      document,
      (response) => {
        this.loadingService.loadingOtherData(false);
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public updateSecondHolderDocument(
    document: AdditionalDocument,
    delegate: (document: AdditionalDocument) => void,
    errorDelegate: (e: any) => void
  ) {
    this.loadingService.loadingOtherData(true);
    this.post<AdditionalDocument>(
      'business/updateSecondHolderDocument',
      document,
      (response) => {
        this.loadingService.loadingOtherData(false);
        delegate(response);
      },
      (e) => errorDelegate(e)
    );
  }

  public downloadDisclosures() {
    this.httpClient
      .get(this.getUrl('business/getdisclosures'), {responseType: 'blob'})
      .subscribe(
        (data) => {
          this.loadingService.loadingUserData(false);
          FileSaver.saveAs(data, 'Disclosures.zip');
        },
        () => {
          this.loadingService.loadingUserData(false);
          console.log('some error when file download');
        }
      );
  }

  public downloadAgreements() {
    this.httpClient
      .get(this.getUrl('business/getagreements'), {responseType: 'blob'})
      .subscribe(
        (data) => {
          this.loadingService.loadingUserData(false);
          FileSaver.saveAs(data, 'Agreements.zip');
        },
        () => {
          this.loadingService.loadingUserData(false);
          console.log('some error when file download');
        }
      );
  }

  public downloadForm(formNo: string, formName: string) {
    this.get<AgreementDocument>(
      'business/downloadfrom?formNo=' + formNo,
      (doc) => {
        const binaryString = window.atob(doc.content);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {type: 'application/pdf'});

        FileSaver.saveAs(blob, formName);
      }
    );
  }

  public submitDocument(user: User, delegate: (user: User) => void): void {
    this.post<User>('business/submitdocument', user, delegate);
  }

  public createAccount(user: User, onFinish: () => void): void {
    this.validationInfo.onUserUpdate.emit({});
    if (this.validationInfo.getValidateState()) {
      this.loadingService.loadingUserData(true);
      this.httpClient
        .post(this.getUrl('business/createaccount'), user, {
          responseType: 'blob',
        })
        .subscribe(
          (data) => {
            // this.loadingService.loadingUserData(false);
            // FileSaver.saveAs(data, 'CreateAccount_Response.zip');
            onFinish();
          },
          () => {
            this.loadingService.loadingUserData(false);
            console.log('some error when file download');
            onFinish();
          }
        );
    }
  }

  public finishApplication(onFinish: () => void, onError?: (error: any) => void): void {
    this.validationInfo.onUserUpdate.emit({});
    if (this.validationInfo.getValidateState()) {
      this.get<any>('business/finishApplication', onFinish, onError);
    } else if (onError) {
      onError(new Error('local-validation-failed'));
    }
  }

  public inviteUser(user: User, phone: string, onComplete: () => void): void {
    this.post<User>('business/invite?inviter=' + phone, user, onComplete);
  }

  public openForm(formNo: string, onComplete: (form: Blob) => void) {
    this.get<AgreementDocument>(
      'business/downloadfrom?formNo=' + formNo,
      (doc) => {
        const binaryString = window.atob(doc.content);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {type: 'application/pdf'});

        onComplete(blob);
      }
    );
  }

  public sendDocuments(phone: string, onComplete: () => void) {
    this.get('users/senddocuments?phone=' + phone, onComplete);
  }

  public canInvite(phone: string, onComplete: (result: boolean) => void) {
    this.get<boolean>('business/caninvite?inviter=' + phone, onComplete);
  }

  public getSocialConfig(onComplete: (config: SocialConfig) => void) {
    this.get<SocialConfig>('data/getSocialConfig', onComplete);
  }

  public getImage(type: string, onComplete: (result: any) => void) {
    this.get(`data/getImage?imageType=${type}`, onComplete);
  }

  public getPassword(code: string, username: string) {
    this.get(`users/requestpass?code=${code}&username=${username}`, () => {
    });
  }

  updateAgreements(onComplete: (config: GeneralAgreement[]) => void) {
    const lang = this.storageService.getLocal('lang');
    this.get<GeneralAgreement[]>(
      `business/updateAgreements?lang=${lang}`,
      onComplete
    );
  }

  public validate(
    onComplete: (result: ValidateResult) => void,
    onError: () => void
  ) {
    this.get(`business/validate`, onComplete, onError);
  }

  public getGTM(onComplete: (result: { id: string }) => void) {
    this.get(`data/GetGTM`, onComplete);
  }
}
